// Session expiry, for calls the user just triggered. Redirecting here is safe because they
// are waiting on this very action — unlike the Messages poll, which runs in the background
// and gets a blocking prompt instead (see SessionExpired in MessageCenter). /index is served
// by index(), which pops myuser_Data, so arriving there also clears the dead server session.
export const LOGIN_URL = '/index';

/* A 401 used to navigate straight to LOGIN_URL. That was self-defeating:
   /index POPS myuser_Data, so a TRANSIENT 401 — a race, a request in flight
   during a restart — destroyed a session that was still perfectly valid, with
   no way back. With background polling on the availability and messages pages
   this path is now hit ~120x/hour per open page, so a blip became a logout.

   It now raises a flag instead. MessagesContext registers the handler and
   flips `authFailed`, which shows the blocking SessionExpired prompt; its
   "Sign in again" button does the navigation, so the session is only ever
   cleared by a deliberate user action.

   The return value is unchanged (true → _json returns {}), so none of the 27
   call sites behave any differently. */
let onExpired = null;
let expired = false;
export const setExpiredHandler = (fn) => { onExpired = fn; };
/* _json swallows a 401 (returns {}), so a polling loop never sees an error and
   would keep firing at a dead session. Intervals check this and stand down. */
export const sessionExpired = () => expired;
export const clearSessionExpired = () => { expired = false; };

export const bounceIfExpired = (res) => {
  // 302 is not checked: fetch follows redirects, so res.status is the final one
  if (res.status === 401) {
    expired = true;
    // guarded: a handler that throws (setState after unmount, bad state) must
    // not escape into _json, where it would surface as a confusing error at
    // whichever call site happened to catch the 401 and hide the real cause
    if (onExpired) { try { onExpired(); } catch (e) { /* expiry is still handled below */ } }
    else window.location.href = LOGIN_URL;     // no handler registered yet
    return true;
  }
  return false;
};

export const fetchUserProfile = async () => {
  const response = await fetch('/api/user/profile', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });


  if (!response.ok) {
    if (response.status === 401) {
      return null;
    }
    throw new Error('Failed to fetch user profile');
  }

  const data = await response.json();
  return data.user;
};



export const fetchTraining = async () => {
  const response = await fetch('/api/training-all', {
    credentials: 'include'
  });

  const result = await response.json();
  
  if (result.status !== 'success') { 
    throw new Error('Failed to fetch training');
  }
  
  return result;
};


export const batchAllUsersHashes = async () => {
    const response = await fetch('/api/admin/batch-all-hashes', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to batch hash records');
    }
    return response.json();
};


export const sendComplianceExpiryEmail = async ({ recordId, category, bucket }) => {
  try {
    const response = await fetch("/api/contract-expiry-email", {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "include",
      body: JSON.stringify({ recordId, category, bucket }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("sendContractExpiryEmail failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};

// records: [{ recordId, abn }] -> { status, results: [{ recordId, abn, status, gst, entityName }] }
// force=true re-checks against the ABR, bypassing the server's 24h cache.
export const fetchAbnStatuses = async (records, force = false) => {
  try {
    const response = await fetch("/api/abn-status", {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "include",
      body: JSON.stringify({ records, force }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("fetchAbnStatuses failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};

export const sendAbnStatusEmail = async ({ recordId, bucket }) => {
  try {
    const response = await fetch("/api/abn-status-email", {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "include",
      body: JSON.stringify({ recordId, bucket }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("sendAbnStatusEmail failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};

// items: [{ recordId, name, visaUrl }] -> { status, results:[{recordId,name,visaType,visaExpiry,dateGranted,dob,scanStatus}] }
export const scanVevo = async (items, force = false) => {
  try {
    const response = await fetch("/api/vevo-scan", {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "include",
      body: JSON.stringify({ items, force }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("scanVevo failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};

// PIN-gated. Returns { ok, status, details? , loggedOut?, attemptsLeft?, error? }
export const fetchVevoDetail = async ({ recordId, visaUrls, pin }) => {
  try {
    const response = await fetch("/api/vevo-detail", {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "include",
      body: JSON.stringify({ recordId, visaUrls, pin }),
    });
    const data = await response.json();
    return { ok: response.ok, ...data };
  } catch (err) {
    console.error("fetchVevoDetail failed:", err);
    return { ok: false, status: "error", error: "Network error. Please try again." };
  }
};

export const issueTempPassword = async ({ email, mobile }) => {
    const response = await fetch('/api/admin/issue-temp-password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, mobile })
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to issue temp password');
    }
    return response.json();
};

export const migratePayslips = async () => {
    const response = await fetch('/api/migrate-payslips', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to migrate payslips');
    }
    return response.json();
};





export const fetchPoPending = async () => {
  const response = await fetch('/api/po-pending', { credentials: 'include' });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch PO data');
  }
  return response.json();
};

export const fetchPoEmailQueue = async () => {
  const response = await fetch('/api/po-email-queue', { credentials: 'include' });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch PO email queue');
  }
  return response.json();
};


export const processPoPdf = ({ onProgress, onComplete, onEmpty, onError }) => {
  fetch('/api/abnposlip', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
    .then((res) => {
      if (bounceIfExpired(res)) {
        return null;
      }
      if (!res.ok) {
        return res.json().catch(() => ({})).then((body) => {
          throw new Error(body.error || `HTTP ${res.status}`);
        });
      }
      return res.json();
    })
    .then((response) => {
      if (!response) return;

      if (Number(response.current) >= 0) {
        if (onProgress) onProgress(response.name, Number(response.current));

        if (Number(response.current) === 0) {
          setTimeout(() => {
            if (onComplete) onComplete();
          }, 1000);
        } else {
          setTimeout(() => {
            processPoPdf({ onProgress, onComplete, onEmpty, onError });
          }, 400);
        }
      } else {
        if (onEmpty) onEmpty();
      }
    })
    .catch((err) => {
      if (onError) onError(err.message);
    });
};


export const sendABNPOEmail = ({ onProgress, onComplete, onEmpty, onError }) => {
  fetch('/api/processabnemail', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
    .then((res) => {
      if (bounceIfExpired(res)) {
        return null;
      }
      if (!res.ok) {
        return res.json().catch(() => ({})).then((body) => {
          throw new Error(body.error || `HTTP ${res.status}`);
        });
      }
      return res.json();
    })
    .then((response) => {
      if (!response) return;

      if (Number(response.current) >= 0) {
        if (onProgress) onProgress(response.name, Number(response.current));

        if (Number(response.current) === 0) {
          setTimeout(() => {
            if (onComplete) onComplete();
          }, 1000);
        } else {
          setTimeout(() => {
            sendABNPOEmail({ onProgress, onComplete, onEmpty, onError });
          }, 400);
        }
      } else {
        if (onEmpty) onEmpty();
      }
    })
    .catch((err) => {
      if (onError) onError(err.message);
    });
};

export const verifyPassword = async (password) => {
  const formData = new FormData();
  formData.append('password', password);
 
  const response = await fetch('/api/verifypassword', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
 
  const data = await response.json();
 
  if (!response.ok) {
    throw new Error(data.message || 'Verification failed');
  }
 
  return data;
};

export const updatePassword = async (oldPassword, newPassword) => {
  const formData = new FormData();
  formData.append('oldpassword', oldPassword);
  formData.append('updatepassword', newPassword);
 
  const response = await fetch('/api/updateprofilepassword', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
 
  const data = await response.json();
 
  if (!response.ok) {
    throw new Error(data.message || 'Failed to update password');
  }
 
  return data;
};

const JSON_HEADERS = { "Content-Type": "application/json" };


export const getfetchUsers = async () => {
  try {
    const response = await fetch("/api/user-working", {
      method: "GET",
      headers: JSON_HEADERS,
      credentials: "include",
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("getfetchUsers failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};



export const fetchPersonalDetailsAll = async () => {
  const res = await fetch('/api/personal-details-all', {
    method: 'GET',
    credentials: 'include',
  });
  return res.json();
};



const scRequest = async (url, options = {}) => {
  try {
    const res = await fetch(url, {
      credentials: "include",
      ...options,
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    });
    if (bounceIfExpired(res)) return { status: "error", error: "Session expired" };
    const data = await res.json().catch(() => ({}));
    if (!res.ok && data.status !== "error") {
      return { status: "error", error: data.error || res.statusText || "Request failed" };
    }
    return data;
  } catch (err) {
    return { status: "error", error: err.message || "Network error" };
  }
};
 
// GET all records from the final subbie table (mapped + linked names resolved)
// → { status, data: [ { recordId, name, email, photo, workStatus, firstName,
//      surname, mobile, dateOfBirth, abnNumber, subbie: [{id,name}], activeClients: [names],
//      position, subbieCount, onboardingStatus, working } ] }
export const fetchFinalSubbies = () => scRequest("/api/subcontractor/final");
 
// GET autocomplete sources
// → { status, subbies: [{id,name}], sites: [names], positions: [options] }
export const fetchFinalSubbieOptions = () => scRequest("/api/subcontractor/final/options");
 
// PATCH one record. Only subbie / activeClients / position are accepted.
//   updateFinalSubbie(recordId, { subbie: ["recAbc123", "recDef456"] })   // partner record IDs
//   updateFinalSubbie(recordId, { activeClients: ["Site A"] })            // site names
//   updateFinalSubbie(recordId, { position: "Supervisor" })
// Subbie changes are mirrored into the partner Subbie table: adding appends the
// cleaner to SPID + Name/Email/Phone/Surname; removing only drops the SPID link
// (a subbie left with no links is deleted). The Subbie text is never changed.
// → { status, data: { subbie: [{id,name}], activeClients, position, subbieCount,
//      onboardingStatus, partnerSync: { added, removed, deleted, errors } } }
export const updateFinalSubbie = (recordId, fields) =>
  scRequest(`/api/subcontractor/final/${encodeURIComponent(recordId)}`, {
    method: "PATCH",
    body: JSON.stringify(fields),
  });
 
// POST a new partner Subbie record and link it to this cleaner's Subbie field.
//   createPartnerSubbie(recordId, { subbie, name, surname, email, phone })
// → { status, data: { created: {id, name}, subbie: [{id,name}], subbieCount, onboardingStatus } }
export const createPartnerSubbie = (recordId, fields) =>
  scRequest(`/api/subcontractor/final/${encodeURIComponent(recordId)}/subbie`, {
    method: "POST",
    body: JSON.stringify(fields),
  });
 
// GET every partner Subbie record for the Subbie Editor
// → { status, data: [ { id, subbie, subbieNames: [...], name, email, phone, surname, spid: [cleanerIds], spidOnly } ] }
export const fetchPartnerSubbies = () => scRequest("/api/subcontractor/partner");
 
// PATCH the text fields of a partner record. Pass only the fields that changed;
// each is a list of entries (Subbie joins with " & ", the rest with ", ").
// SPID is never accepted here — links are only changed from the cleaner's side.
//   updatePartnerSubbie("recAbc123", { subbie: ["JN Bolo", "G Calero"], email: ["a@x.com"] })
// → { status, data: { id, name: <Subbie text>, subbieNames,
//      fields: { subbie, name, email, phone, surname }, updated: [keys] } }
export const updatePartnerSubbie = (subbieId, fields) =>
  scRequest(`/api/subcontractor/partner/${encodeURIComponent(subbieId)}`, {
    method: "PATCH",
    body: JSON.stringify({ fields }),
  });
 
// Convenience: edit only the Subbie text
export const updatePartnerSubbieName = (subbieId, names) =>
  updatePartnerSubbie(subbieId, { subbie: names });
 
/* ============================================================================
 * Activated — same shape as the Test & Tag report functions.
 * JSON_HEADERS is the existing constant in utils/api.js.
 * ==========================================================================*/
 
// GET the three main-database views tagged by category
// → { status, data: [ { recordId, category: "casual"|"employee"|"contractor", categoryLabel,
//      name, photo, email, mobile, startWork, workPosition,
//      activation: "active"|"name_mismatch"|"contact_mismatch"|"pending", loginName } ],
//     category is "casual" | "employee" | "contractor" | "new"
//     counts: { casual, employee, contractor, new, casualPending, employeePending, contractorPending, newPending } }
export const fetchActivated = async () => {
  try {
    const response = await fetch("/api/activated", { method: "GET", headers: JSON_HEADERS, credentials: "include" });
    const data = await response.json();
    if (!response.ok) return { status: "error", error: data.error || `Request failed (${response.status})` };
    return data;
  } catch (err) { console.error("fetchActivated failed:", err); return { status: "error", error: "Network error. Please try again." }; }
};
 
export const activatedReportPreview = async (filterHeader, rows, summary) => {
  try {
    const response = await fetch("/api/activated-report-preview", {
      method: "POST", headers: JSON_HEADERS, credentials: "include",
      body: JSON.stringify({ filterHeader, rows, summary }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    const blob = await response.blob();
    return { status: "success", blob };
  } catch (err) {
    console.error("activatedReportPreview failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};
 
export const activatedReportSend = async (filterHeader, rows, cc, summary) => {
  try {
    const response = await fetch("/api/activated-report-send", {
      method: "POST", headers: JSON_HEADERS, credentials: "include",
      body: JSON.stringify({ filterHeader, rows, cc, summary }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("activatedReportSend failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};


  
export const fetchManagedUsers = async () => {
  try {
    const response = await fetch("/api/users-manage", {
      method: "GET",
      headers: JSON_HEADERS,
      credentials: "include",
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("fetchManagedUsers failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};
 

export const updateManagedUser = async (recordId, fields) => {
  try {
    const response = await fetch(`/api/users-manage/${recordId}`, {
      method: "PATCH",
      headers: JSON_HEADERS,
      credentials: "include",
      body: JSON.stringify({ fields }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("updateManagedUser failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};
 

export const uploadUserAttachment = async (recordId, fieldKey, file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(
      `/api/users-manage/${recordId}/attachments/${fieldKey}`,
      {
        method: "POST",
        credentials: "include",
        body: formData, // no Content-Type header — browser sets the boundary
      }
    );
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("uploadUserAttachment failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};
 


export const downloadUsersManage_ = async (workStatus) => {
  const response = await fetch(`/api/download-users-manage/${workStatus}`, {
    credentials: "include",
  });
  if (!response.ok) {
    let message = "Download failed";
    try { const err = await response.json(); message = err.error || message; } catch (_) {}
    throw new Error(message);
  }
  return response.blob();
};







export const fetchCleanerData = async ({ template }) => {
  const response = await fetch('/api/filter-cleanerdata', {
    method: 'POST',
    credentials: 'include',
  headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      templateType: template
    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch cleaner data');
  }

  return response.json();
};


export const fetchGroupAccess = async () => {
  const response = await fetch('/api/group-access', {
    credentials: 'include'
  });

  const result = await response.json();
  
  if (result.status !== 'success') {
    throw new Error('Failed to fetch group access');
  }

  return result;
};


export const fetchReplacements = async ({ monthYear }) => {
  const response = await fetch('/api/filter-replacements', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      monthYear: monthYear
    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch replacements');
  }

  return response.json();
};




export const handleTimeSubmit = async (dataTime) => {
  
  const formData = new FormData();

  formData.append('recordId', dataTime.recordId);
  formData.append('cleanerReplacing', dataTime.cleanerReplacing);
  formData.append('timeReplacing', dataTime.timeReplacing);
  
  

  
  

  const response = await fetch('/api/update-replacement-time', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to update replacement');
  }
  
  return response.json();
};


export const fetchLeave = async ({ monthYear }) => {
  const response = await fetch('/api/filter-leave', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      monthYear: monthYear
    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch leave');
  }

  return response.json();
};



export const downloadPeriodical = async (monthYear) => {
  const response = await fetch(`/api/download-periodical/${monthYear}`);
  if (!response.ok) throw new Error('Download failed');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `periodical_${monthYear}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};


export const downloadExtrawork = async (monthYear) => {
  const response = await fetch(`/api/download-extrawork/${monthYear}`);
  if (!response.ok) throw new Error('Download failed');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `extrawork_${monthYear}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadSouthyarra = async (monthYear) => {
  const response = await fetch(`/api/download-southyarra/${monthYear}`);
  if (!response.ok) throw new Error('Download failed');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `southyarra_${monthYear}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export const downloadAbnPayments = async (monthYear) => {
  const response = await fetch(`/api/downloadABN-payments/${monthYear}`); 
  if (!response.ok) throw new Error('Download failed');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `paymentsabn_${monthYear}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadReplacement = async (monthYear) => {
  const response = await fetch(`/api/download-replacement/${monthYear}`); 
  if (!response.ok) throw new Error('Download failed');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `replacement_${monthYear}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadRequestLeave = async (monthYear) => {
  const response = await fetch(`/api/download-requestleave/${monthYear}`); 
  if (!response.ok) throw new Error('Download failed');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `requestleave_${monthYear}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};




export const downloadAbncalculations_ = async (monthYear) => {
  const response = await fetch(`/api/download-abn_calculations/${monthYear}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    let message = 'Download failed';
    try { const err = await response.json(); message = err.error || message; } catch (_) {}
    throw new Error(message);
  }
  return response.blob();
};

export const downloadAbnpurchaseorder_ = async (monthYear) => {
  const response = await fetch(`/api/download-abn_purchaseorder/${monthYear}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    let message = 'Download failed';
    try { const err = await response.json(); message = err.error || message; } catch (_) {}
    throw new Error(message);
  }
  return response.blob();
};





export async function fetchHigherViewStatic() {
  const res = await fetch('/api/higherview/static', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Higher View static load failed (${res.status})`);
  return res.json();
}

export async function fetchHigherViewFYAll(fyStart) {
  const res = await fetch(`/api/higherview/fyall/${encodeURIComponent(fyStart)}`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Higher View load failed (${res.status})`);
  return res.json();
}



const _hvBlob = async (url) => {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    let message = 'Download failed';
    try { const err = await response.json(); message = err.error || message; } catch (_) {}
    throw new Error(message);
  }
  return response.blob();
};

export const downloadDashboardEmployee = () => _hvBlob('/api/download-higherview-employee');
export const downloadDashboardSubbie = () => _hvBlob('/api/download-higherview-subbie');
export const downloadCurrentMonth = () => _hvBlob('/api/download-higherview-current');

export const downloadHigherviewFY = (fyStart, expenses = [], month = null) => {
  const params = new URLSearchParams();
  if (expenses && expenses.length) params.set('expenses', expenses.join(','));
  if (month != null) params.set('month', String(month));
  const qs = params.toString();
  return _hvBlob(`/api/download-higherview/${encodeURIComponent(fyStart)}${qs ? `?${qs}` : ''}`);
};



export const fetchSouthyarra = async ({ monthYear }) => {
  const response = await fetch('/api/filter-southyarra', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      monthYear: monthYear
    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch south yarra data');
  }

  return response.json();
};


export const handleSouthyarraSubmit = async (dataLeave) => {
  const formData = new FormData();
  formData.append('recordId', dataLeave.recordId);
  formData.append('workstatus', dataLeave.work_status);
  formData.append('completedate', dataLeave.complete_date);
  formData.append('cleaner', dataLeave.cleaner_user);
  formData.append('email', dataLeave.email_user);
  formData.append('extrawork', dataLeave.extra_work);
  formData.append('extraworkchange', dataLeave.extra_work_change);
  formData.append('extraworkday', dataLeave.extra_work_day);
  formData.append('mightbeadmin', dataLeave.mylogin);

  const response = await fetch('/api/update-departure', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    if (response.status === 403) {
      throw new Error(err.error || 'This job is locked — ready for invoicing.');
    }
    throw new Error(err.error || 'Failed to update departure record');
  }
  return response.json();
}


export const deleteSouthyarraRecords = async (records) => {
  try {
    const response = await fetch('/api/southyarra/delete-records', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ records }),
    });
 
    const data = await response.json().catch(() => ({}));
 
    if (!response.ok) {
      throw new Error(data?.message || `Delete failed (HTTP ${response.status})`);
    }
 
    return data;
  } catch (err) {
    console.error('deleteSouthyarraRecords error:', err);
    return { success: false, message: err.message || 'Network error' };
  }
};



const handleDepartureResponse = async (res, fallback) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || fallback);
  }
  return data;
};
 

export const fetchDeparture = async ({ monthYear }) => {
  const response = await fetch('/api/filter-departure', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ monthYear }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch departure data');
  }
  return response.json();
};
 
// POST /api/departure/bulk-approve  { recordIds: [...] }
export const bulkApproveDepartures = async (recordIds) => {
  const res = await fetch('/api/departure/bulk-approve', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recordIds }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok && !data.approved) throw new Error(data.message || 'Bulk approve failed');
  return data;              // { success, approved, failed, messages, message }
};

export const submitDeparture = async (payload) => {
  const res = await fetch('/api/departure/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  return handleDepartureResponse(res, 'Submission failed');
};
 

export const updateDeparture = async (recordId, payload) => {
  const res = await fetch(`/api/departure/update/${recordId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  return handleDepartureResponse(res, 'Update failed');
};
 

export const uploadDepartures = async (file, commit = false) => {
  try {
    const form = new FormData();
    form.append('file', file);
    form.append('commit', commit ? 'true' : 'false');
    const res = await fetch('/api/departure/upload', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { status: 'error', error: data.error || `Request failed (${res.status})`, ...data };
    }
    return data;
  } catch (err) {
    console.error('uploadDepartures failed:', err);
    return { status: 'error', error: 'Network error. Please try again.' };
  }
};

export const sendDepartureReport = async (rows, source = 'form', cc = []) => {
  const res = await fetch('/api/departure/report-send', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ rows, source, cc }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status !== 'success') {
    throw new Error(data.error || 'Email failed');
  }
  return data;
};

 
// POST /api/departure/extrawork-approve  { recordId, decision: 'Approve'|'Decline' }
// The server finds the extrawork record itself via igluRecordId = recordId
export const approveExtrawork = async (recordId, decision) => {
  const res = await fetch('/api/departure/extrawork-approve', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recordId, decision }),
  });
  const data = await res.json().catch(() => ({}));
  const explicitFail = data.success === false || data.status === 'error';
  if (!res.ok || explicitFail) throw new Error(data.message || data.error || `Update failed (${res.status})`);
  return data;
};


 



export const fetchExtrawork = async ({ monthYear }) => {
  const response = await fetch('/api/filter-extrawork', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      monthYear: monthYear
    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch extrawork');
  }

  return response.json();
};

export const handleLeaveSubmit = async (dataLeave) => {
  const formData = new FormData();

  formData.append('recordId', dataLeave.recordId);
  formData.append('employee', dataLeave.employee);
  formData.append('request', dataLeave.request);
  formData.append('return', dataLeave.return);
  formData.append('type_leave', dataLeave.type_leave);
  formData.append('status_approval', dataLeave.status_approval);
  formData.append('time_worked', String(dataLeave.time_worked ?? '0'));

  
  if (dataLeave.myFile) {
    formData.append('myFile', dataLeave.myFile);
  }

  const response = await fetch('/api/update-leave-doc', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to update leave record');
  }

  return response.json();
};

export const fetchPayslips_pending = async () => {
  const response = await fetch('/api/payslips-pending', {
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch payslip data');
  }
  return response.json();
};



// =========================================================================
// Bulk payslip email sender — recursive fetch with React callbacks.
// =========================================================================

export const sendTFNEmail = ({ onProgress, onComplete, onEmpty, onError }) => {
  fetch('/api/processemail', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
    .then((res) => {
      if (bounceIfExpired(res)) {
        return null;
      }
      if (!res.ok) {
        return res.json().catch(() => ({})).then((body) => {
          throw new Error(body.error || `HTTP ${res.status}`);
        });
      }
      return res.json();
    })
    .then((response) => {
      if (!response) return;   // auth redirect already happened

      if (Number(response.current) >= 0) {
        if (onProgress) onProgress(response.name, Number(response.current));

        if (Number(response.current) === 0) {
          setTimeout(() => {
            if (onComplete) onComplete();
          }, 1000);
        } else {
          setTimeout(() => {
            sendTFNEmail({ onProgress, onComplete, onEmpty, onError });
          }, 400);
        }
      } else {
        if (onEmpty) onEmpty();
      }
    })
    .catch((err) => {
      if (onError) onError(err.message);
    });
};


// =========================================================================
// Single payslip email sender — one-shot fetch with React callbacks.
// =========================================================================

export const sendTFNEmailSingle = ({ record_id, onSuccess, onError }) => {
  fetch(`/api/processemail/${encodeURIComponent(record_id)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
    .then((res) => {
      if (bounceIfExpired(res)) {
        return null;
      }
      if (!res.ok) {
        return res.json().catch(() => ({})).then((body) => {
          throw new Error(body.error || `HTTP ${res.status}`);
        });
      }
      return res.json();
    })
    .then((response) => {
      if (!response) return;
      if (response.error) {
        if (onError) onError(response.error);
        return;
      }
      if (onSuccess) onSuccess(response.name);
    })
    .catch((err) => {
      if (onError) onError(err.message);
    });
};


export const fetchEmail_data = async () => {
  const response = await fetch('/api/payslips-email-queue', {
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch email queue');
  }
  return response.json();
};




export const handleExtraworkSubmit = async (dataLeave) => {
  const formData = new FormData();

  formData.append('recordId', dataLeave.recordId);
  formData.append('workdate', dataLeave.workdate);
  formData.append('dayworked', dataLeave.dayworked);
  formData.append('timeworked', dataLeave.timeworked);

  
  

  const response = await fetch('/api/update-extrawork', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to update extrawork record');
  }

  return response.json();
};




export const getfetchSites = async () => {
  const response = await fetch('/api/sites');
  return response.json();
};

export const updateSite = async (recordId, fields) => {
  const response = await fetch(`/api/sites/${recordId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  return response.json();
};

export const createSite = async (site) => {
  const response = await fetch('/api/sites', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(site),
  });
  return response.json();
};

export const fetchSites = async () => {
  const response = await fetch('api/training/sites', {
    credentials: 'include'
  });


  const result = await response.json();
  if (!result) {
    
    throw new Error('Failed to fetch sites');
  }

  return result;
};

export const addTraining = async (trainingData) => {
  const endpoint = '/api/new-training';
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(trainingData), 
  });

  const result = await response.json();
  
  if (result.status !== 'success') { 
    throw new Error('Failed to create new record');
  }
  
  return result;
};


export const updateTraining = async (trainingData) => {
  const endpoint = '/api/update-training';
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(trainingData), 
  });

  const result = await response.json();
  
  if (result.status !== 'success') { 
    throw new Error('Failed to create update record');
  }
  
  return result;
};

export const addSite = async (trainingData) => {
  const endpoint = '/api/add-site';
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({site: trainingData}), 
  });

  const result = await response.json();
  
  if (result.status !== 'success') { 
    throw new Error('Failed to create update record');
  }
  
  return result;
};


export const deleteTraining = async (recordId) => {
  const endpoint = '/api/delete-training';
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ id: recordId }),
  });
  
  const result = await response.json();
  
  if (result.status !== 'success') { 
    throw new Error('Failed to delete record');
  }
  
  return result;
};


// ─────────────────────────────────────────────────────────────────────────
// TestNTag API — add to utils/api.js
// ─────────────────────────────────────────────────────────────────────────

const parseError = async (response, fallback) => {
  let message = fallback;
  try { const err = await response.json(); message = err.error || message; } catch (_) {}
  return new Error(message);
};

export const requestDeleteAuthTestNTag = async (id) => {
  const response = await fetch(`/api/testntag/${id}/request-delete-auth`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw await parseError(response, 'Failed to request delete authorisation');
  }
  return response.json();
};
 
/* Delete the record if (and only if) the entered 6-digit code matches the
   record's "Authorise Delete" field. Verification happens server-side. */
export const authoriseDeleteTestNTag = async (id, code) => {
  const response = await fetch(`/api/testntag/${id}/authorised-delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) {
    throw await parseError(response, 'Failed to authorise delete');
  }
  return response.json();
};



export const fetchTestNTag = async () => {
  const response = await fetch('/api/testntag', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw await parseError(response, 'Failed to load TestNTag records');
  }
  return response.json();
};

/* Staff pool for the reassign modal — [{ name, job }] */
export const fetchTestNTagStaff = async () => {
  const res = await fetch('/api/testntag', { credentials: 'include' });
  const data = await res.json();
  if (!res.ok || data.status !== 'success') {
    throw new Error(data.error || 'Failed to load staff list');
  }
  return data.staff;
};

export const reassignTestNTag = async ( assignments ) => {
  const response = await fetch("/api/testntag/reassign", {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      assignments
      

    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to reassign records');
  }

  return response.json();
};


export const createTestNTag = async (payload) => {
  const response = await fetch('/api/testntag/new', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw await parseError(response, 'Failed to create TestNTag record');
  }
  return response.json();
};


export const updateTestNTag = async (id, payload) => {
  const response = await fetch(`/api/testntag/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw await parseError(response, 'Failed to update TestNTag record');
  }
  return response.json();
};


export const deleteTestNTag = async (id) => {
  const response = await fetch(`/api/testntag/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    throw await parseError(response, 'Failed to delete TestNTag record');
  }
  return response.json();
};


export const downloadTestNTagExcel = async (rows) => {
  const response = await fetch('/api/download-testntag', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  if (!response.ok) {
    let message = 'Download failed';
    try { const err = await response.json(); message = err.error || message; } catch (_) {}
    throw new Error(message);
  }
  return response.blob();
};
export const fetchPayments = async () => {
  const response = await fetch('/api/payments-all', {
    credentials: 'include'
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch payments');
  }

  return response.json();
};

export const tntReportPreview = async (filterHeader, rows, summary) => {
  try {
    const response = await fetch("/api/tnt-report-preview", {
      method: "POST", headers: JSON_HEADERS, credentials: "include",
      body: JSON.stringify({ filterHeader, rows, summary }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    const blob = await response.blob();
    return { status: "success", blob };
  } catch (err) {
    console.error("tntReportPreview failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};
 
export const tntReportSend = async (filterHeader, rows, cc, summary) => {
  try {
    const response = await fetch("/api/tnt-report-send", {
      method: "POST", headers: JSON_HEADERS, credentials: "include",
      body: JSON.stringify({ filterHeader, rows, cc, summary }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("tntReportSend failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};

export const fetchABNPayments = async ({ monthYear }) => {
  const response = await fetch('/api/filter-abnpayments', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      monthYear: monthYear
    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch abn payments');
  }

  return response.json();
};


export const fetchTFNPayments = async ({ monthYear }) => {
  const response = await fetch('/api/filter-tfnpayments', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      monthYear: monthYear
    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch tfn payments');
  }

  return response.json();
};

export const downloadTFNcalculations_ = async (monthYear) => {
  const response = await fetch(`/api/download-tfn_calculations/${monthYear}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    let message = 'Download failed';
    try { const err = await response.json(); message = err.error || message; } catch (_) {}
    throw new Error(message);
  }
  return response.blob();
};


export const downloadTFNcalculations_super = async (monthYear) => {
  const response = await fetch(`/api/download-tfn_super/${monthYear}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    let message = 'Download failed';
    try { const err = await response.json(); message = err.error || message; } catch (_) {}
    throw new Error(message);
  }
  return response.blob();
};

export const createTFNBank = async ({ monthYear }) => {
  const response = await fetch('/api/filter-tfnbank', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      monthYear: monthYear
    })
  });

  if (!response.ok) {
    // error responses are still JSON
    let message = 'Failed to create bank file';
    try {
      const err = await response.json();
      message = err.error || message;
    } catch (_) {
      // non-JSON error body; keep the default message
    }
    throw new Error(message);
  }

  const approvedCount = parseInt(response.headers.get('X-Approved-Count') || '0', 10);
  const blob = await response.blob();
  return { blob, approvedCount };
};




/* Flip the ticked Payments records to HPN (Hold Payment Now).
 * recordIds are the recordid1 values (index 14) — they point at the
 * subbie table that get_subbie_approveall() patches. */
export const submitAbnHold = async ({ recordIds }) => {
  const response = await fetch('/api/submit-abnhold', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      recordIds: recordIds
    })
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to hold payments');
  }
  return response.json(); // { status: 'success', updated: n }
};


export const createABNBank = async ({ monthYear }) => {
  const response = await fetch('/api/filter-abnbank', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      monthYear: monthYear
    })
  });

  if (!response.ok) {
    // error responses are still JSON
    let message = 'Failed to create bank file';
    try {
      const err = await response.json();
      message = err.error || message;
    } catch (_) {
      // non-JSON error body; keep the default message
    }
    throw new Error(message);
  }

  const approvedCount = parseInt(response.headers.get('X-Approved-Count') || '0', 10);
  const blob = await response.blob();
  return { blob, approvedCount };
};
export const fetchPeriodical = async ({ monthYear }) => {
  const response = await fetch('/api/filter-periodical', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      monthYear: monthYear
    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch periodicals');
  }

  return response.json();
};


export const handlePeriodicalMgmtSubmit = async (data) => {
  const response = await fetch('/api/updatesingleperiod', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Update failed');
  return response.json();
};
 
// Used by staff — employees / subcontractors (PeriodicalCleanerModal).
// Sends only the status + attend payload to the cleaner endpoint.
export const handlePeriodicalCleanerSubmit = async (data) => {
  const response = await fetch('/api/updatesingleperiodcleaner', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Update failed');
  return response.json();
};





export const addNewPeriodical = async (formData) => {
  const response = await fetch('/api/create-new-periodical', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(formData),
  });
  const result = await response.json();
  if (result.status !== 'success') {
    throw new Error('Failed to create new periodical');
  }
  return result;
};

export const fetchPeriodicalSites = async () => {
  const response = await fetch('/api/periodical-sites', {
    credentials: 'include'
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch sites');
  }
  return response.json();
};



// ─────────────────────────────────────────────
//  utils/api.js — Replacement API calls (add to existing api.js)
// ─────────────────────────────────────────────

export const fetchReplacementTfnShifts = async () => {
  const res = await fetch('/api/replacement/tfn-shifts');
  if (!res.ok) throw new Error('Failed to fetch TFN shifts');
  return res.json();
};

export const fetchReplacementAbnShifts = async () => {
  const res = await fetch('/api/replacement/abn-shifts');
  if (!res.ok) throw new Error('Failed to fetch ABN shifts');
  return res.json();
};

export const fetchReplacementExisting = async () => {
  const res = await fetch('/api/replacement/existing');
  if (!res.ok) throw new Error('Failed to fetch existing replacements');
  return res.json();
};

export const fetchReplacementAbnNames = async () => {
  const res = await fetch('/api/replacement/abn-names');
  if (!res.ok) throw new Error('Failed to fetch ABN names');
  return res.json();
};

export const submitReplacement = async (payload) => {
  const res = await fetch('/processreplacement', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Submission failed');
  }
  return res.json();
};




export const checkNewUser = async ({ workStatus, email, mobile }) => {
  const response = await fetch("/api/check-new-user", {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      work_status: workStatus,
      email: email,
      mobile: mobile

    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch record');
  }

  return response.json();
};


export const addNewUser = async (formData) => {
  const endpoint = '/api/create-new-onboarding';
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(formData), 
  });

  const result = await response.json();
  
  if (result.status !== 'success') { 
    throw new Error('Failed to create new record');
  }
  
  return result;
};

export const addNewClient = async (siteName, siteAddress) => {
  const formData = new FormData();
  formData.append('sitename', siteName);
  formData.append('siteaddress', siteAddress);
 
  const response = await fetch('/api/createnewsite', {
    method: 'POST',
    body: formData,
  });
 
  const mydata = await response.json();
 
  if (!response.ok) {
    throw new Error(mydata.message || 'Failed to update new site');
  }
 
  return mydata;
};
 

 
export const fetchOnboardNewUsersOnly = async () => {
  const res = await fetch('/api/onboarding-newusers-only', {
    method: 'GET',
    credentials: 'include',
  });
  return res.json();
};
 
export const fetchOnboardActive = async () => {
   const res = await fetch('/api/onboard-active', {
     method: 'GET',
     credentials: 'include',
   });
   return res.json();
 };


export const deleteOnboardActive = async (recordId) => {
  try {
    const res = await fetch(`/api/onboard-active/${encodeURIComponent(recordId)}`, {
      method: "DELETE",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok && !body.status) {
      return { status: "error", message: `Delete failed (${res.status})` };
    }
    return body;
  } catch (err) {
    return { status: "error", message: err.message || "Network error" };
  }
};


export const updateOnboardWorking = async (recordId, working) => {
  const res = await fetch(`/api/onboard-active/${recordId}/working`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ working }),
  });
  return res.json();
};


export const fetchOnboardingAll = async () => {
  const res = await fetch('/api/onboarding-details-all', {
    method: 'GET',
    credentials: 'include',
  });
  return res.json();
};

export const updateOnboardingAll = async (payload) => {
  const res = await fetch('/api/onboarding-details-all', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  return res.json();
};

export async function submitResignation(pdfBlob, name) {
  const body = new FormData();
  body.append("myDoc", pdfBlob, `resignation_${name}.pdf`);
  body.append("name", name);
 
  const response = await fetch("/api/resignationuser", {
    method: "POST",
    body,
  });
 
  const result = await response.json();
 
  if (!response.ok) {
    throw new Error(result.message || "Failed to submit resignation");
  }
 
  return result;

};


export const fetchPersonalDetails = async () => {
  const response = await fetch('/api/getpersonaldetails', {
    credentials: 'include'
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch personal details');
  }
  return response.json();
};

export const updatePersonalDetails = async (payload) => {
  const response = await fetch('/api/updatepersonaldetails', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || `Update failed (${response.status})`);
  }
  return data;
};


export const fetchTickets = async ({ monthYear }) => {
  const response = await fetch('/api/filter-tickets', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      monthYear: monthYear
    })
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Failed to fetch tickets');
  }

  return response.json();
};

export const updateTickets = async (formData) => {
  const res = await fetch('/api/update-ticket', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!res.ok) throw new Error('Failed to update record');
  return res.json();
};

export const fetchClientTickets = async ({ monthYear }) => {
  const res = await fetch('/api/clienttickets', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ monthYear }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Failed to load tickets (${res.status})`);
  return data;
};
 
// POST /api/clientticket/update — FormData from ApproveTicketModal
export const approveClientTicket = async (formData) => {
  const res = await fetch('/api/clientticket/update', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status !== 'success') {
    throw new Error(data.message || data.error || 'Update failed');
  }
  return data;
};




export const importTimesheets = async (file, commit = false) => {
  try {
    const form = new FormData();
    form.append("file", file);
    form.append("commit", commit ? "true" : "false");
    
    const response = await fetch("/api/import-timesheets", {
      method: "POST",
      credentials: "include",
      body: form,
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("importTimesheets failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};

export const timesheetAlias = async ({ freshopsName, systemName, decision }) => {
  try {
    const response = await fetch("/api/timesheet-alias", {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "include",
      body: JSON.stringify({ freshopsName, systemName, decision }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("timesheetAlias failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};

export const fetchTimesheetsMonth = async (monthYear) => {
  try {
    const response = await fetch(`/api/timesheets-month?my=${encodeURIComponent(monthYear)}`, {
      method: "GET",
      headers: JSON_HEADERS,
      credentials: "include",
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("fetchTimesheetsMonth failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};



export const fetchTkEmailCandidates = async () => {
  try {
    const response = await fetch("/api/tk-email-candidates", { method: "GET", headers: JSON_HEADERS, credentials: "include" });
    const data = await response.json();
    if (!response.ok) return { status: "error", error: data.error || `Request failed (${response.status})` };
    return data;
  } catch (err) { console.error("fetchTkEmailCandidates failed:", err); return { status: "error", error: "Network error. Please try again." }; }
};



export const tkReportPreview = async (filterHeader, rows,summary) => {
  try {
    const response = await fetch("/api/tk-report-preview", { method: "POST", headers: JSON_HEADERS,
      credentials: "include", body: JSON.stringify({ filterHeader, rows, summary }) });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return { status: "success", blob: await response.blob() };
  } catch (err) { console.error("tkReportPreview failed:", err); return { status: "error", error: "Network error. Please try again." }; }
};

export const tkReportSend = async (filterHeader, rows, recipients, summary) => {
  // ...body: JSON.stringify({ filterHeader, rows, recipients, summary })...

  try {
    const response = await fetch("/api/tk-report-send", { method: "POST", headers: JSON_HEADERS,
      credentials: "include", body: JSON.stringify({ filterHeader, rows, recipients, summary })});
    const data = await response.json();
    if (!response.ok) return { status: "error", error: data.error || `Request failed (${response.status})` };
    return data;
  } catch (err) { console.error("tkReportSend failed:", err); return { status: "error", error: "Network error. Please try again." }; }
};

export const fetchTkCcDefault = async () => {
  try {
    const response = await fetch("/api/tk-cc-default", { method: "GET", headers: JSON_HEADERS, credentials: "include" });
    const data = await response.json();
    if (!response.ok) return { status: "error", error: data.error || `Request failed (${response.status})` };
    return data;
  } catch (err) { console.error("fetchTkCcDefault failed:", err); return { status: "error", error: "Network error. Please try again." }; }
};

export const saveTkCcDefault = async (chips) => {
  try {
    const response = await fetch("/api/tk-cc-default", { method: "POST", headers: JSON_HEADERS,
      credentials: "include", body: JSON.stringify({ chips }) });
    const data = await response.json();
    if (!response.ok) return { status: "error", error: data.error || `Request failed (${response.status})` };
    return data;
  } catch (err) { console.error("saveTkCcDefault failed:", err); return { status: "error", error: "Network error. Please try again." }; }
};

export const fetchTimesheetDatesNeeded = async () => {
  try {
    const response = await fetch("/api/timesheet-dates-needed", { method: "GET", headers: JSON_HEADERS, credentials: "include" });
    const data = await response.json();
    if (!response.ok) return { status: "error", error: data.error || `Request failed (${response.status})` };
    return data;
  } catch (err) { console.error("fetchTimesheetDatesNeeded failed:", err); return { status: "error", error: "Network error. Please try again." }; }
};


export const syReportPreview = async (filterHeader, rows, summary) => {
  try {
    const response = await fetch("/api/sy-report-preview", {
      method: "POST", headers: JSON_HEADERS, credentials: "include",
      body: JSON.stringify({ filterHeader, rows, summary }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    const blob = await response.blob();
    return { status: "success", blob };
  } catch (err) {
    console.error("syReportPreview failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};
 
export const syReportSend = async (filterHeader, rows, cc, summary) => {
  try {
    const response = await fetch("/api/sy-report-send", {
      method: "POST", headers: JSON_HEADERS, credentials: "include",
      body: JSON.stringify({ filterHeader, rows, cc, summary }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("syReportSend failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};


export const fetchSyUsers = async () => {
  try {
    const response = await fetch("/api/sy-users", {
      method: "GET", headers: JSON_HEADERS, credentials: "include",
    });
    const data = await response.json();
    if (!response.ok) return { status: "error", error: data.error || `Request failed (${response.status})` };
    return data;
  } catch (err) {
    console.error("fetchSyUsers failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};
 
export const syCorrection = async ({ recordId, cleaner, email, completeDate, reset }) => {
  try {
    const response = await fetch("/api/sy-correction", {
      method: "POST", headers: JSON_HEADERS, credentials: "include",
      body: JSON.stringify({ recordId, cleaner, email, completeDate, reset: !!reset }),
    });
    const data = await response.json();
    if (!response.ok) return { status: "error", error: data.error || `Request failed (${response.status})` };
    return data;
  } catch (err) {
    console.error("syCorrection failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};

export const pdReportPreview = async (filterHeader, rows, summary) => {
  try {
    const response = await fetch("/api/pd-report-preview", {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ filterHeader, rows, summary }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    const blob = await response.blob();
    return { status: "success", blob };
  } catch (err) {
    console.error("pdReportPreview failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};
 
export const pdReportSend = async (filterHeader, rows, cc, summary) => {
  try {
    const response = await fetch("/api/pd-report-send", {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ filterHeader, rows, cc, summary }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { status: "error", error: data.error || `Request failed (${response.status})` };
    }
    return data;
  } catch (err) {
    console.error("pdReportSend failed:", err);
    return { status: "error", error: "Network error. Please try again." };
  }
};




// ─────────────────────────────────────────────────────────────
// Add to utils/api.js — Work Availability
// ─────────────────────────────────────────────────────────────

const _json = async (res) => {
  if (bounceIfExpired(res)) return {};
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `${res.status} ${res.statusText}`);
  return body;
};

/* Every lifecycle write below also answers with `email`: the automation email the server
   tried to send for that stage — { sent, stage, stageKey, to, email, detail }. The pages
   feed it to pushEmail() in components/Toasts.jsx for the green / red toast. */
/** GET /api/casual/access → { casual, recordId, name, workingWithChildren, today, homePostcode } */
export const fetchCasualAccess = (force = false) =>
  fetch(`/api/casual/access${force ? '?force=1' : ''}`, { credentials: 'include' }).then(_json);

/** GET /api/casual/availability → { entries } (this casual, last 14 days onward, with jobs) */
export const fetchCasualAvailability = (since) =>
  fetch(`/api/casual/availability${since ? `?since=${since}` : ''}`, { credentials: 'include' }).then(_json);

/** POST /api/casual/availability (builder payload) → { submissionId, created, entries, email } */
export const submitCasualAvailability = (payload) =>
  fetch('/api/casual/availability', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(_json);

/** PATCH /api/casual/availability/slot/:recordId { date?, start, end } → { slot, entries, jobs }
 *  start / end are 24h "HH:MM"; the date only moves while the day has no job. */
export const updateCasualSlot = (recordId, body) =>
  fetch(`/api/casual/availability/slot/${encodeURIComponent(recordId)}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }).then(_json);

/** DELETE /api/casual/availability/:submissionId → { withdrawn, entries, email } */
export const withdrawCasualAvailability = (submissionId) =>
  fetch(`/api/casual/availability/${encodeURIComponent(submissionId)}`, {
    method: 'DELETE', credentials: 'include',
  }).then(_json);

/** PATCH /api/casual/assignment/:id  { status: 'accepted' | 'declined', reason? } → { assignment, email } */
export const respondToAssignment = (id, status, reason = '') =>
  fetch(`/api/casual/assignment/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, reason }),
  }).then(_json);

// ─────────────────────────────────────────────────────────────
// Supervisor side
// ─────────────────────────────────────────────────────────────

/** GET /api/super/slots → { slots } (Open Slots view, today onward) */
export const fetchSuperSlots = () =>
  fetch('/api/super/slots', { credentials: 'include' }).then(_json);

/** GET /api/super/jobs?bucket=responses|roster → { jobs } */
export const fetchSuperJobs = (bucket = 'responses') =>
  fetch(`/api/super/jobs?bucket=${bucket}`, { credentials: 'include' }).then(_json);

/** POST /api/super/jobs { availabilityId, jobAddress, jobSuburb, jobPostcode, jobLat, jobLng,
 *  jobStart, jobFinish, contactPerson, contactPhone, notes } → { job } */
export const assignSuperJob = (body) =>
  fetch('/api/super/jobs', {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }).then(_json);

/** PATCH /api/super/jobs/:id { action: 'approve' | 'cancel', reason? } → { job, email } */
export const superJobAction = (id, action, reason = '') =>
  fetch(`/api/super/jobs/${id}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, reason }),
  }).then(_json);

/** GET /api/super/snljobs?status=Available → { jobs } (omit status for all) */
export const fetchSnlJobs = (status) =>
  fetch(`/api/super/snljobs${status ? `?status=${status}` : ''}`, { credentials: 'include' }).then(_json);

/** POST /api/super/snljobs (job draft) → { job } */
export const createSnlJobApi = (body) =>
  fetch('/api/super/snljobs', {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }).then(_json);

/** PATCH /api/super/snljobs/:id { status } → { job } */
export const patchSnlJobStatus = (id, status, reason = '') =>
  fetch(`/api/super/snljobs/${id}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status, reason }),
  }).then(_json);


/** PATCH /api/super/snljobs/:id { fields: { jobStart?, jobEnd?, notes? } } → { job }
 *  (live Pending / Accepted assignments take the new values; an Approved job refuses) */
export const patchSnlJobFields = (id, fields) =>
  fetch(`/api/super/snljobs/${id}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fields }),
  }).then(_json);

/** POST /api/super/resync → { jobs, days } (how many statuses were corrected) */
export const resyncStatuses = () =>
  fetch('/api/super/resync', { method: 'POST', credentials: 'include' }).then(_json);

// ─────────────────────────────────────────────────────────────
// Messages — casual side
// ─────────────────────────────────────────────────────────────

/** GET /api/casual/messages → { threads } (one per Job Assignment) */
export const fetchCasualThreads = () =>
  fetch('/api/casual/messages', { credentials: 'include' }).then(_json);

/** POST /api/casual/messages/:assignmentId { text } → { message, email } */
export const sendCasualMessage = (assignmentId, text) =>
  fetch(`/api/casual/messages/${assignmentId}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
  }).then(_json);

/** PATCH /api/casual/messages/:assignmentId/seen → { seen } */
export const markCasualThreadSeen = (assignmentId) =>
  fetch(`/api/casual/messages/${assignmentId}/seen`, { method: 'PATCH', credentials: 'include' }).then(_json);

// ─────────────────────────────────────────────────────────────
// Messages — supervisor side
// ─────────────────────────────────────────────────────────────

/** GET /api/super/messages → { threads } (last 60 days, all casuals) */
export const fetchSuperThreads = () =>
  fetch('/api/super/messages', { credentials: 'include' }).then(_json);

/** POST /api/super/messages/:assignmentId { text } → { message, email } */
export const sendSuperMessage = (assignmentId, text) =>
  fetch(`/api/super/messages/${assignmentId}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
  }).then(_json);

/** PATCH /api/super/messages/:assignmentId/seen → { seen } */
export const markSuperThreadSeen = (assignmentId) =>
  fetch(`/api/super/messages/${assignmentId}/seen`, { method: 'PATCH', credentials: 'include' }).then(_json);


export const geocodeSuburb = async (q, address = '') => {
  const params = new URLSearchParams({ q });
  if (address) params.set('address', address);
  const res = await fetch(`/api/super/geocode?${params.toString()}`, { credentials: 'include' });
  let body = null;
  try { body = await res.json(); } catch (_) { /* non-JSON error page */ }
  if (res.status === 401 || res.status === 403) throw new Error(body?.message || `geocode ${res.status}`);
  return body || { status: 'error', message: `Lookup failed (${res.status})` };
};


// utils/api.js additions — global Messages
const _json2 = async (res) => {
  let body2 = null;
  try { body2 = await res.json(); } catch (_) { /* html error page */ }
  if (!res.ok) throw new Error(body2?.message || `messages ${res.status}`);
  return body2;
};

export const fetchMessageDirectory = (force = false) =>
  fetch(`/api/messages/directory${force ? '?force=1' : ''}`, { credentials: 'include' }).then(_json2);
 
export const fetchUnreadMessages = () =>
  fetch('/api/messages/unread', { credentials: 'include' }).then(_json2);
 
export const fetchMessageThreads = () =>
  fetch('/api/messages/threads', { credentials: 'include' }).then(_json2);
 
// New conversation: recipients + title.  Reply: threadId (+ toEmail when the owner replies to ONE person;
// empty = everyone in the thread; recipients always reply to the owner).
export const sendMessages = (recipients, text, audience = '', title = '', threadId = '', toEmail = '') =>
  fetch('/api/messages/send', {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recipients, text, audience, title, threadId, toEmail }),
  }).then(_json2);
 
export const markThreadSeen = (key, ids = []) =>
  fetch('/api/messages/seen', {
    method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, ids }),
  }).then(_json2);


